/**
 * Escolha do serviço fiscal na emissão da NFSe.
 *
 * Clínicas com mais de um CNAE (consulta, exame, cirurgia) pagam alíquotas
 * diferentes por tipo de serviço. Antes de emitir, este diálogo pergunta com
 * qual serviço a nota deve sair — já sugerindo o que os procedimentos cobrados
 * indicam e avisando quando a cobrança mistura serviços de tributação distinta.
 *
 * Também define o profissional citado na nota e mostra a descrição que vai
 * para a Focus, editável — a nota precisa caracterizar o serviço prestado
 * (tratamento + profissional com registro no conselho), não só o medicamento.
 *
 * Sem serviço fiscal nem profissional cadastrado a clínica não vê nada: o fluxo
 * segue direto com os parâmetros de Configurações → NFSe.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ReceiptLong as ReceiptIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { todayYmdBr } from '../../utils/localCalendarDate';
import {
  clinicColors,
  clinicDialogBannerSx,
  clinicDialogCancelButtonSx,
  clinicDialogContentSx,
  clinicDialogFooterSx,
  clinicDialogPaperSx,
  clinicDialogPrimaryButtonSx,
  clinicInputFieldSx,
  clinicMonoFont,
} from '../../pages/Clinic/clinicUi';

export interface NfseServiceCode {
  id: string;
  name: string;
  description?: string | null;
  codigo_cnae?: string | null;
  item_lista_servico?: string | null;
  codigo_tributacao_nacional?: string | null;
  codigo_tributario_municipio?: string | null;
  aliquota_iss?: number | null;
  codigo_nbs?: string | null;
  ibs_cbs_situacao_tributaria?: string | null;
  ibs_cbs_classificacao_tributaria?: string | null;
  codigo_indicador_operacao?: string | null;
  is_default?: boolean;
}

export interface NfseProfessionalOption {
  id: string;
  name: string;
  credential: string;
  specialty?: string | null;
}

export interface NfseDescriptionSuggestion {
  base_description: string;
  professionals: NfseProfessionalOption[];
  suggested_professional_id: string | null;
}

/** Corpo enviado nas rotas de emissão. */
export interface NfseEmitChoice {
  service_code_id: string | null;
  professional_id?: string | null;
  description?: string | null;
  /** Competência da prestação (YYYY-MM-DD). */
  issue_date?: string | null;
}

const MAX_DESCRIPTION_LENGTH = 1000;

/** Mesmo texto que o backend monta quando a descrição não é editada. */
export function composeNfseDescription(base: string, professional: NfseProfessionalOption | null): string {
  const services = String(base || '').trim().replace(/[.;,\s]+$/, '');
  const clause = professional
    ? `realizado por ${professional.name}${professional.credential ? `, ${professional.credential}` : ''}`
    : '';
  const text = clause ? `${services}, ${clause}.` : services ? `${services}.` : '';
  return text.slice(0, MAX_DESCRIPTION_LENGTH);
}

interface BreakdownItem {
  serviceCodeId: string | null;
  serviceCodeName: string;
  amount: number;
  procedures: string[];
}

export interface NfseServiceContext {
  receiptId?: string | null;
  transactionId?: string | null;
  budgetId?: string | null;
  procedureIds?: string[] | null;
}

interface Props {
  open: boolean;
  context: NfseServiceContext;
  /** Sugestão já carregada por quem abriu o diálogo, evitando uma segunda busca. */
  preloaded?: NfseServiceSuggestion | null;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: (choice: NfseEmitChoice) => void;
}

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export function describeServiceCode(code: NfseServiceCode): string {
  const parts = [
    code.codigo_cnae ? `CNAE ${code.codigo_cnae}` : '',
    code.item_lista_servico ? `LC 116 ${code.item_lista_servico}` : '',
    code.codigo_tributacao_nacional ? `cTribNac ${code.codigo_tributacao_nacional}` : '',
    code.aliquota_iss != null ? `ISS ${Number(code.aliquota_iss)}%` : '',
    code.ibs_cbs_classificacao_tributaria
      ? `IBS/CBS ${code.ibs_cbs_situacao_tributaria || '—'}/${code.ibs_cbs_classificacao_tributaria}`
      : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

export interface NfseServiceSuggestion {
  options: NfseServiceCode[];
  suggestion: NfseServiceCode | null;
  breakdown: BreakdownItem[];
  mixed: boolean;
  description?: NfseDescriptionSuggestion | null;
}

/** Busca a sugestão; devolve null quando a clínica não usa serviços fiscais. */
export async function fetchNfseServiceSuggestion(
  context: NfseServiceContext,
): Promise<NfseServiceSuggestion | null> {
  const params: Record<string, string> = {};
  if (context.receiptId) params.receipt_id = context.receiptId;
  if (context.transactionId) params.transaction_id = context.transactionId;
  if (context.budgetId) params.budget_id = context.budgetId;
  if (context.procedureIds?.length) params.procedure_ids = context.procedureIds.join(',');

  const { data } = await axios.get(getApiUrl('/clinic/nfse/service-codes/suggestion'), { params });
  return (data?.data as NfseServiceSuggestion) || null;
}

const NfseServiceCodeDialog: React.FC<Props> = ({
  open,
  context,
  preloaded = null,
  submitting = false,
  onClose,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<NfseServiceCode[]>([]);
  const [breakdown, setBreakdown] = useState<BreakdownItem[]>([]);
  const [mixed, setMixed] = useState(false);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState('');
  const [baseDescription, setBaseDescription] = useState('');
  const [professionals, setProfessionals] = useState<NfseProfessionalOption[]>([]);
  const [professionalId, setProfessionalId] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionEdited, setDescriptionEdited] = useState(false);
  const [competenciaDate, setCompetenciaDate] = useState(todayYmdBr());

  const apply = useCallback((data: NfseServiceSuggestion | null) => {
    setOptions(data?.options || []);
    setBreakdown(data?.breakdown || []);
    setMixed(Boolean(data?.mixed));
    setSelected(data?.suggestion?.id || '');
    setBaseDescription(data?.description?.base_description || '');
    setProfessionals(data?.description?.professionals || []);
    setProfessionalId(data?.description?.suggested_professional_id || '');
    setDescriptionEdited(false);
  }, []);

  const load = useCallback(async () => {
    if (preloaded) {
      apply(preloaded);
      return;
    }
    setLoading(true);
    setError('');
    try {
      apply(await fetchNfseServiceSuggestion(context));
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Não foi possível carregar os serviços fiscais.');
    } finally {
      setLoading(false);
    }
  }, [apply, context, preloaded]);

  useEffect(() => {
    if (open) {
      setCompetenciaDate(todayYmdBr());
      void load();
    }
  }, [open, load]);

  const selectedCode = options.find((o) => o.id === selected) || null;
  const selectedProfessional = useMemo(
    () => professionals.find((p) => p.id === professionalId) || null,
    [professionals, professionalId],
  );

  useEffect(() => {
    if (!descriptionEdited) setDescription(composeNfseDescription(baseDescription, selectedProfessional));
  }, [baseDescription, selectedProfessional, descriptionEdited]);

  const handleProfessionalChange = (id: string) => {
    setProfessionalId(id);
    setDescriptionEdited(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: clinicDialogPaperSx }}>
      <Box sx={clinicDialogBannerSx}>
        <ReceiptIcon />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Emitir nota fiscal</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            Confira serviço fiscal, profissional e descrição antes de emitir.
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={clinicDialogContentSx}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}

            {mixed && (
              <Alert severity="warning">
                Os procedimentos cobrados pertencem a serviços fiscais diferentes. A nota sai inteira
                com o serviço escolhido abaixo — para tributar cada parte corretamente, emita uma nota
                por serviço.
              </Alert>
            )}

            {options.length > 0 && (
            <FormControl fullWidth size="small" sx={clinicInputFieldSx}>
              <InputLabel>Serviço fiscal</InputLabel>
              <Select
                value={selected}
                label="Serviço fiscal"
                onChange={(e) => setSelected(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>Usar os parâmetros gerais da clínica</em>
                </MenuItem>
                {options.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                    {option.is_default ? ' (padrão)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            )}

            {selectedCode && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(13, 148, 136, 0.06)',
                  border: '1px solid rgba(13, 148, 136, 0.18)',
                }}
              >
                <Typography variant="caption" sx={{ color: clinicColors.slate, fontWeight: 700 }}>
                  Códigos aplicados
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: clinicMonoFont, mt: 0.5 }}>
                  {describeServiceCode(selectedCode) || 'Herda os parâmetros da clínica'}
                </Typography>
                {selectedCode.description && (
                  <Typography variant="caption" sx={{ color: clinicColors.slate, display: 'block', mt: 0.5 }}>
                    {selectedCode.description}
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              label="Data de competência"
              type="date"
              value={competenciaDate}
              onChange={(e) => setCompetenciaDate(e.target.value.slice(0, 10))}
              fullWidth
              size="small"
              sx={clinicInputFieldSx}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayYmdBr() }}
              helperText="Mês/ano em que o serviço é considerado prestado. Use a data atual se o pagamento foi antigo."
            />

            {professionals.length > 0 && (
              <FormControl fullWidth size="small" sx={clinicInputFieldSx}>
                <InputLabel>Profissional responsável na nota</InputLabel>
                <Select
                  value={professionalId}
                  label="Profissional responsável na nota"
                  onChange={(e) => handleProfessionalChange(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>Não citar profissional</em>
                  </MenuItem>
                  {professionals.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                      {p.credential ? ` — ${p.credential}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedProfessional && !selectedProfessional.credential && (
              <Alert severity="warning">
                Este profissional está sem número de registro no cadastro (CRM, COREN…). Complete em
                Profissionais para que o registro saia na nota.
              </Alert>
            )}

            <TextField
              label="Descrição do serviço na nota"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH));
                setDescriptionEdited(true);
              }}
              multiline
              minRows={3}
              fullWidth
              size="small"
              sx={clinicInputFieldSx}
              helperText={
                descriptionEdited
                  ? `${description.length}/${MAX_DESCRIPTION_LENGTH} · editada manualmente`
                  : 'Descreva o tratamento realizado, não só o medicamento. O texto padrão vem da "Descrição na nota fiscal" de cada procedimento.'
              }
            />

            {breakdown.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: clinicColors.slate, fontWeight: 700 }}>
                  Procedimentos cobrados
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                  {breakdown.map((item) => (
                    <Box
                      key={item.serviceCodeId || 'sem-servico'}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                    >
                      <Chip
                        size="small"
                        label={item.serviceCodeName}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: item.serviceCodeId ? 'rgba(13, 148, 136, 0.12)' : 'rgba(180, 83, 9, 0.12)',
                          color: item.serviceCodeId ? '#0F766E' : '#B45309',
                        }}
                      />
                      <Typography variant="caption" sx={{ color: clinicColors.slate }}>
                        {item.procedures.join(', ') || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ ml: 'auto', fontWeight: 700, color: clinicColors.navy }}
                      >
                        {formatCurrency(item.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={clinicDialogFooterSx}>
        <Button onClick={onClose} sx={clinicDialogCancelButtonSx} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onConfirm({
              service_code_id: selected || null,
              professional_id: professionalId || null,
              description: description.trim() || null,
              issue_date: competenciaDate || todayYmdBr(),
            })
          }
          disabled={loading || submitting}
          sx={clinicDialogPrimaryButtonSx}
        >
          {submitting ? 'Emitindo…' : 'Emitir NFSe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NfseServiceCodeDialog;
